import { makeStyles, Theme } from '@material-ui/core';
import React, { useEffect } from 'react';

import { getAccountsList, getSelectedAccount } from './queries';
import AccountDropdown from './AccountDropdown';
import { AccountGroupDropdownComponent } from '../account-group-dropdown';
import { AccountDetail } from '../../__generated__/pge-types';
import useAccountCustomer from '../../hooks/useAccountCustomer';
import useAccountDetailList from '../../hooks/useAccountDetailList';
import useAuthQuery from '../../hooks/useAuthQuery';
import useSelectedAccountParams from '../../hooks/useSelectedAccountParams';
import { useIsMobile } from '../../util/style-utils';
import useAccountListHeader from '../account-summary/multi-accounts/useAccountListHeader';
import useAccountsStore from '../../hooks/useAccountsStore';

type Props = {
  showSelectedAccountOnly?: boolean;
  onSelect?: (account: AccountDetail) => void;
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    '& > *:first-child': {
      marginBottom: theme.spacing(2),
    },
  },
}));

export default function AccountDropdownContainer({
  onSelect,
  showSelectedAccountOnly = false,
}: Props) {
  const { customer, groups, loading: loadingCustomer } = useAccountCustomer();
  const {
    selectedGroupId,
    accountParams,
    setSelectedAccountParams,
    setSelectedAccountGroup,
    loading,
  } = useSelectedAccountParams();

  const {
    accountListParams,
    onAccountSearch,
    onAccountSort,
    onAccountPageChange,
    onResetParams,
  } = useAccountListHeader();

  const {
    detailList,
    loading: loadingAccounts,
    accounts,
    totalCount,
  } = useAccountDetailList(getAccountsList, selectedGroupId, {
    accountListParams,
  });

  const { setAccountDisconnectedStatus } = useAccountsStore();
  const isMobile = useIsMobile();

  const groupCount =
    groups.find(grp => grp.groupId === selectedGroupId)?.numberOfAccounts || 0;

  const { data, loading: loadingSelected } = useAuthQuery(getSelectedAccount, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
    },
    skip: !accountParams || !detailList || loading,
  });

  const selectedAccount = data?.getAccountDetails?.[0];

  useEffect(() => {
    setAccountDisconnectedStatus(
      selectedAccount?.serviceConnectivity?.isReconnectInProgress,
      selectedAccount?.serviceConnectivity?.isDisconnected,
      selectedAccount?.serviceConnectivity?.isEligibleForReconnect,
    );
  }, [selectedAccount]);

  const classes = useStyles();

  if (!customer || loadingCustomer || !selectedGroupId) {
    return null;
  }

  const accountSection = (
    <AccountDropdown
      onAccountSearch={onAccountSearch}
      onAccountSort={onAccountSort}
      onAccountPageChange={onAccountPageChange}
      accountListParams={accountListParams}
      totalCount={totalCount || 0}
      loading={loadingAccounts || loadingSelected || !accountParams}
      accounts={accounts}
      selectedAccount={selectedAccount}
      onSelect={account => {
        setSelectedAccountParams(selectedGroupId, account);
        if (onSelect !== undefined) {
          onSelect(account);
        }
      }}
      showSelectedAccountOnly={showSelectedAccountOnly || groupCount <= 1}
      isMobile={isMobile}
    />
  );

  if (
    showSelectedAccountOnly ||
    (customer &&
      customer?.accountMeta &&
      customer?.accountMeta?.totalAccounts <= 1)
  ) {
    return accountSection;
  }

  return (
    <div className={classes.container}>
      <AccountGroupDropdownComponent
        groups={groups}
        selectedGroupId={selectedGroupId}
        onSelect={grpId => {
          onResetParams();
          setSelectedAccountGroup(grpId);
        }}
      />
      {accountSection}
    </div>
  );
}
